'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowDownAZ, ClockArrowDown, Search, UsersRound } from 'lucide-react'
import { DialogSidebar } from '@elmnts/ui/dialog-sidebar'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@elmnts/ui/input-group'
import { ToggleGroup, ToggleGroupItem } from '@elmnts/ui/toggle-group'
import { Toggle } from '@elmnts/ui/toggle'
import { Button } from '@elmnts/ui/button'
import { Skeleton } from '@elmnts/ui/skeleton'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@elmnts/ui/empty'
import { usePresence } from '@/providers/PresenceContext'
import { usePresenceRoster, type RosterEntry } from '@/hooks/presence/usePresenceRoster'
import { usePresenceTick } from '@/hooks/presence/usePresenceTick'
import { useCanaisDosUsuarios, useChatsDosUsuarios } from '@/hooks/navigation/useCanaisDosUsuarios'
import { usePermissions } from '@/hooks/usePermissions'
import { OndeEstaNaEquipe, PresenceRow } from '@/components/Presence/PresenceRow'
import { IfHasPermission } from '@/components/guards'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import { presenceOf, presenceStatusLabel } from '@/lib/presence/presenceTime'
import type { PresenceStatus } from '@/types/presence'
import { cn } from '@/lib/utils'
import './styles.css'

type Filter = 'all' | PresenceStatus
const RANK: Record<PresenceStatus, number> = { online: 0, afk: 1, offline: 2 }
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'online', label: 'Online' },
  { key: 'afk', label: 'Ausente' },
  { key: 'offline', label: 'Offline' },
]
const STATES: PresenceStatus[] = ['online', 'afk', 'offline']
const ROW_CLS = 'presence-drawer__row flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left'

/** Puro: filtra por estado + busca; ordena online→afk→offline, depois por nome (ou tempo). */
export function filterRoster(
  roster: RosterEntry[],
  filter: Filter,
  query: string,
  sortByDuration: boolean,
): RosterEntry[] {
  const q = query.toLowerCase().trim()
  return roster
    .filter((u) => filter === 'all' || presenceOf(u) === filter)
    .filter((u) => !q || u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
    .sort((a, b) => {
      const byState = RANK[presenceOf(a)] - RANK[presenceOf(b)]
      if (byState !== 0) return byState
      if (sortByDuration) return (a.last_seen_at ?? 0) - (b.last_seen_at ?? 0) // mais antigo primeiro
      return a.name.localeCompare(b.name)
    })
}

/**
 * Puro: o que o estado vazio diz e qual saída oferece. O botão nomeia SÓ o que
 * está ativo — "Limpar busca" sem filtro, "Ver todos" sem busca: oferecer
 * limpar uma busca que ninguém fez não faz sentido.
 */
export function saidaDoVazio(
  query: string,
  filter: Filter,
): { descricao: string; acao: string | null; limpaBusca: boolean; limpaFiltro: boolean } {
  const q = query.trim()
  const limpaBusca = q !== ''
  const limpaFiltro = filter !== 'all'
  const descricao = limpaBusca
    ? `Nenhum colega bate com "${q}".`
    : limpaFiltro
      ? `Ninguém ${presenceStatusLabel(filter).toLowerCase()} agora.`
      : 'A equipe ainda não tem ninguém.'
  const acao = limpaBusca && limpaFiltro ? 'Limpar busca e ver todos' : limpaBusca ? 'Limpar busca' : limpaFiltro ? 'Ver todos' : null
  return { descricao, acao, limpaBusca, limpaFiltro }
}

/**
 * Board ao vivo da equipe num `DialogSidebar` (lateral ⇄ modal, fixável). Monta
 * só com `users:presence:read` (gate no componente, não no provider). A linha
 * navega pra `/admin/users/{db_id}`, que chama `GET /users/{id}` (`users:read`)
 * — então sem esse grant ela vira `<div>` com o mesmo conteúdo. `ADMIN_ACCESS`
 * NÃO serve: o atendente abre ele por `channels:read` e caía num 403.
 */
export function PresenceDrawer() {
  const { isDrawerOpen, closeDrawer } = usePresence()
  const { roster, counts, isError, isLoading } = usePresenceRoster()
  const { hasPermission, isRevalidating } = usePermissions()
  const now = usePresenceTick()
  // Canais abertos por user, calculado UMA vez pro board (não por linha). Vazio sem o gate triplo.
  const canais = useCanaisDosUsuarios()
  const chats = useChatsDosUsuarios()
  const router = useRouter()
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [sortByDuration, setSortByDuration] = useState(false)

  const visible = filterRoster(roster, filter, query, sortByDuration)
  // Mesmo critério do `canLink` do UserDisplay: snapshot velho não decide controle de UI.
  const canLink = !isRevalidating && hasPermission(USERS_PERMISSIONS.READ)
  const go = (id: string) => {
    closeDrawer()
    router.push(`/admin/users/${id}`)
  }
  const vazio = saidaDoVazio(query, filter)

  return (
    <IfHasPermission require={USERS_PERMISSIONS.PRESENCE_READ}>
      <DialogSidebar
        open={isDrawerOpen}
        onOpenChange={(o) => !o && closeDrawer()}
        title="Equipe"
        description={isError ? undefined : `${counts.online} online agora`}
        defaultSidebarWidth={360}
        dialogWidthClass="max-w-full rounded-none md:max-w-[440px] md:rounded-lg"
      >
        {isError ? (
          // 403/500 viravam lista vazia, que a tela lia como "ninguém online" —
          // afirmação falsa sobre o time. O painel continua abrindo e fechando.
          <p role="alert" className="text-sm text-muted-foreground">
            Não foi possível carregar a presença da equipe.
          </p>
        ) : (
          <div className="presence-drawer flex min-h-0 flex-col gap-3">
            {/* Busca e filtros grudam no topo: o corpo do DialogSidebar é quem rola.
                O sticky para na borda do PADDING (py-2) do corpo, e as linhas
                apareciam nessa faixa por cima da busca: `-top-2 -mt-2 pt-2` cola
                o fundo na borda do scroll. */}
            <div className="presence-drawer__topo sticky -top-2 z-10 -mt-2 flex flex-col gap-2 pt-2 pb-1">
              <div className="flex items-center gap-2">
                <InputGroup className="min-w-0 flex-1">
                  <InputGroupAddon>
                    <Search aria-hidden="true" />
                  </InputGroupAddon>
                  <InputGroupInput
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar por nome ou @usuário"
                    aria-label="Buscar na equipe"
                  />
                </InputGroup>
                {/* O ÍCONE muda com o estado (A–Z ⇄ relógio): só o realce do pressed não se lia. */}
                <Toggle
                  variant="outline"
                  pressed={sortByDuration}
                  onPressedChange={setSortByDuration}
                  aria-label="Ordenar por tempo no estado"
                  title={sortByDuration ? 'Ordenado por mais tempo no estado' : 'Ordenado por nome (A–Z)'}
                  className="shrink-0"
                >
                  {sortByDuration ? <ClockArrowDown aria-hidden="true" /> : <ArrowDownAZ aria-hidden="true" />}
                </Toggle>
              </div>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={filter}
                // Radix manda '' ao clicar no item já ativo: segmentado nunca fica sem seleção.
                onValueChange={(v) => v && setFilter(v as Filter)}
                aria-label="Filtrar por estado"
                className="w-full"
              >
                {FILTERS.map((f) => (
                  // flex-1 + min-w-0 dividem a largura por igual e deixam o rótulo encolher no painel estreito.
                  <ToggleGroupItem key={f.key} value={f.key} className="min-w-0 flex-1 shrink gap-1 px-1.5 text-xs">
                    <span className="truncate">{f.label}</span>
                    {f.key !== 'all' && <span className="tabular-nums text-muted-foreground">{counts[f.key]}</span>}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
            {isLoading && roster.length === 0 ? (
              <div role="status" aria-label="Carregando equipe" className="flex flex-col gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                    <Skeleton className="size-8 rounded-lg" />
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Skeleton className="h-3 w-2/5" />
                      <Skeleton className="h-2.5 w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : visible.length === 0 ? (
              <Empty className="py-8">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UsersRound aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>Ninguém encontrado</EmptyTitle>
                  <EmptyDescription>{vazio.descricao}</EmptyDescription>
                </EmptyHeader>
                {vazio.acao && (
                  <EmptyContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (vazio.limpaBusca) setQuery('')
                        if (vazio.limpaFiltro) setFilter('all')
                      }}
                    >
                      {vazio.acao}
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            ) : (
              <div className="flex flex-col gap-4">
                {STATES.map((state) => {
                  const grupo = visible.filter((user) => presenceOf(user) === state)
                  if (grupo.length === 0) return null
                  return (
                    <section key={state} aria-labelledby={`presence-group-${state}`}>
                      <h3 id={`presence-group-${state}`} className="presence-drawer__group">
                        {`${presenceStatusLabel(state)} — ${grupo.length}`}
                      </h3>
                      <ul className="flex flex-col gap-0.5">
                        {grupo.map((user) => {
                          const onde = { canais: canais.get(user.db_id), chats: chats.get(user.db_id) }
                          const body = <PresenceRow user={user} now={now} {...onde} />
                          return (
                            <li key={user.db_id}>
                              <OndeEstaNaEquipe user={user} now={now} {...onde}>
                                {canLink ? (
                                  <button type="button" onClick={() => go(user.db_id)} className={cn(ROW_CLS, 'hover:bg-accent')}>
                                    {body}
                                  </button>
                                ) : (
                                  <div className={ROW_CLS}>{body}</div>
                                )}
                              </OndeEstaNaEquipe>
                            </li>
                          )
                        })}
                      </ul>
                    </section>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </DialogSidebar>
    </IfHasPermission>
  )
}
